/**
 * NewUserHero — the signed-out / no-roadmap hero, redesigned per the
 * 2026-07-26 homepage redesign spec: problem-first (ProblemCard sits beside
 * the headline instead of a product-flow diagram), honest CTA copy (the old
 * "Start free diagnostic" label implied an immediate no-signup action; the
 * assessment route is ProtectedRoute-gated, so the label now says what
 * actually happens), and CTAs ordered "See how it works" before "Get my
 * free roadmap" — the explore-first funnel order the design decided on.
 */

import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ProblemCard from "./ProblemCard";
import { coral } from "../../theme/tokens";

export default function NewUserHero() {
  const { t } = useTranslation("home");
  return (
    <Box
      component="section"
      sx={{
        display: "grid",
        gap: { xs: 4, lg: 6 },
        gridTemplateColumns: { xs: "1fr", lg: "1.05fr 0.95fr" },
        alignItems: "center",
      }}
    >
      <Stack spacing={2.5}>
        <Typography
          component="span"
          sx={{
            fontSize: 10.5,
            fontWeight: 500,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          {t("NewUserHero.eyebrow")}
        </Typography>

        <Typography
          component="h1"
          sx={{
            fontSize: { xs: 36, md: 48, lg: 56 },
            // Deliberate, scoped exception to the platform's 400/500-only weight
            // cap (David confirmed 2026-07-26, pre-flight review): this H1 is the
            // single loudest moment on the page and appears exactly once. Not
            // applied anywhere else in this plan or the codebase.
            fontWeight: 600,
            lineHeight: 1.02,
            letterSpacing: "-0.035em",
            color: "text.primary",
            m: 0,
          }}
        >
          <Trans
            i18nKey="NewUserHero.heroTitle"
            t={t}
            components={[
              <Box
                component="em"
                key="em0"
                sx={{ color: "secondary.main", fontStyle: "normal" }}
              />,
            ]}
          />
        </Typography>

        <Typography
          sx={{
            fontSize: 18,
            lineHeight: 1.55,
            color: "text.secondary",
          }}
        >
          <Trans
            i18nKey="NewUserHero.bodyText"
            t={t}
            components={[
              <Box
                component="strong"
                key="strong0"
                sx={{ color: "text.primary", fontWeight: 500 }}
              />,
            ]}
          />
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <Button
            component={RouterLink}
            to="#how"
            variant="contained"
            disableElevation
            sx={{
              fontWeight: 500,
              textTransform: "none",
              px: 2.5,
              py: 1.5,
              // Light mode: tint.coralBg (#fef7f6) reads as visually
              // indistinguishable from the page background (#f7fafc) — the
              // exact gap spec §6 flagged for this button. coral[100] gives
              // real contrast. Dark mode's tint.coralBg is already fine
              // (verified against the dark background), so it's unchanged.
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "tint.coralBg" : coral[100],
              color: "secondary.dark",
              "&:hover": { bgcolor: "secondary.main", color: "common.white" },
            }}
          >
            {t("NewUserHero.seeHowItWorks")}
          </Button>
          <Button
            component={RouterLink}
            to="/career-transition/assessment"
            variant="contained"
            color="secondary"
            endIcon={<ArrowForwardIcon />}
            sx={{ fontWeight: 500, textTransform: "none", px: 2.5, py: 1.5 }}
          >
            {t("NewUserHero.startFreeDiagnostic")}
          </Button>
        </Stack>

        <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: "text.secondary" }}>
          {t("NewUserHero.trustLine")}
        </Typography>
      </Stack>

      <ProblemCard />
    </Box>
  );
}
